import type { VpIconSize } from '@vtmn-play/react'
import type { CatalogImage } from '@/types/catalog/common'
import { useQuery } from '@tanstack/react-query'
import { VpIcon, VpLoader } from '@vtmn-play/react'
import { useEffect, useRef, useState } from 'react'
import { queryKeys } from '@/query/queryKeys'
import { buildGalleryImageUrl } from '@/utils/media'

interface ProductImageProps {
  alt?: string
  checkAvailability?: boolean
  image?: CatalogImage | null
  imageClassName?: string
  imageTestId?: string
  placeholderClassName?: string
  placeholderIconSize?: VpIconSize
  placeholderTestId?: string
  width?: number
  onError?: (imageUrl: string) => void
}

export function ProductImage({
  alt = '',
  checkAvailability = false,
  image,
  imageClassName,
  imageTestId,
  placeholderClassName,
  placeholderIconSize = 48,
  placeholderTestId,
  width,
  onError,
}: Readonly<ProductImageProps>) {
  const [failedImageUrl, setFailedImageUrl] = useState<string>()
  const notifiedAvailabilityErrorRef = useRef<unknown>(null)
  const imageUrl = buildGalleryImageUrl(image, width)

  const availabilityQuery = useQuery({
    queryKey: queryKeys.productImageAvailability(imageUrl),
    enabled: checkAvailability && Boolean(imageUrl),
    queryFn: async ({ signal }) => {
      if (!imageUrl)
        throw new Error('Image URL is required')

      const response = await fetch(imageUrl, { method: 'HEAD', signal })
      if (!response.ok)
        throw new Error(`Image availability check failed: ${response.status}`)

      return imageUrl
    },
    retry: false,
    staleTime: 1000 * 60 * 15,
  })

  useEffect(() => {
    const error = availabilityQuery.error
    if (!checkAvailability || !imageUrl || !error || availabilityQuery.isFetching || notifiedAvailabilityErrorRef.current === error)
      return

    notifiedAvailabilityErrorRef.current = error
    onError?.(imageUrl)
  }, [availabilityQuery.error, availabilityQuery.isFetching, checkAvailability, imageUrl, onError])

  if (checkAvailability && availabilityQuery.isFetching) {
    return (
      <div className={placeholderClassName} aria-busy="true">
        <VpLoader />
      </div>
    )
  }

  if (!imageUrl || failedImageUrl === imageUrl || (checkAvailability && availabilityQuery.isError)) {
    return (
      <div className={placeholderClassName} data-testid={placeholderTestId} aria-hidden="true">
        <VpIcon name="image" size={placeholderIconSize} />
      </div>
    )
  }

  return (
    <img
      src={imageUrl}
      alt={alt}
      className={imageClassName}
      data-testid={imageTestId}
      onError={() => {
        setFailedImageUrl(imageUrl)
        onError?.(imageUrl)
      }}
    />
  )
}
